import { Injector, NgModuleRef, PlatformRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LeafHostModule } from 'app/leaf/leaf-host.module';
import { resetLeafDisplayedPath, setLeafDisplayedPath } from 'app/react-bridge/leaf-displayed-path';
import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { DesignSystemSigmaService } from 'app/services/design-system-sigma.service';
import { criarEmissorPageview } from 'app/services/posthog/posthog-pageview';
import { cleanupAngularOverlays, resetBodyLayout } from './bootstrap-cleanup.utils';
import { createBootstrapLifecycle } from './bootstrap-lifecycle';

let platformRef: PlatformRef | null = null;
let moduleRef: NgModuleRef<LeafHostModule> | null = null;
let routerEventsSub: Subscription | null = null;

const lifecycle = createBootstrapLifecycle({
  bootstrap: bootstrapLeaf,
  teardown: teardownLeaf,
});

/** Injector da folha ativa, usado pelo seam de delegação de auth (react-bridge). Null quando a folha não está montada. */
export function getLeafInjector(): Injector | null {
  return moduleRef?.injector ?? null;
}

/** Monta a feature Angular no container sem FullLayout. Idempotente. */
export function mountAngularLeaf(container: HTMLElement): Promise<void> {
  return lifecycle.mount(container);
}

async function bootstrapLeaf(container: HTMLElement): Promise<void> {
  if (moduleRef) return; // já montada: nada a fazer

  // Remover qualquer <app-leaf-host> órfão antes de criar um novo, para
  // garantir um único host mesmo após um unmount imperfeito anterior.
  document.querySelectorAll('app-leaf-host').forEach((el) => el.remove());

  const host = document.createElement('app-leaf-host');
  container.appendChild(host);

  try {
    platformRef = platformBrowserDynamic();
    moduleRef = await platformRef.bootstrapModule(LeafHostModule);
  } catch (err) {
    // Bootstrap parcialmente executado: destruir plataforma e limpar o host
    // para que uma nova tentativa parta de estado consistente.
    platformRef?.destroy();
    platformRef = null;
    moduleRef = null;
    document.querySelectorAll('app-leaf-host').forEach((el) => el.remove());
    throw err;
  }

  trackLeafDisplayedRoute(moduleRef);
}

/**
 * Espelha no store a rota real que a folha exibe: o AngularLeafHost usa esse sinal
 * para saber se a folha derivou do pathname do shell. As navegações internas da
 * folha (pushState) não notificam o React Router, então este é o único ponto onde o
 * shell fica sabendo qual rota a folha está mostrando.
 *
 * O rastreamento é auxiliar e nunca pode derrubar o mount: se o Router não estiver
 * resolvível (não esperado com a folha real), apenas não alimenta o store.
 */
function trackLeafDisplayedRoute(ref: NgModuleRef<LeafHostModule>): void {
  const router = resolveLeafRouter(ref);
  if (!router) {
    return;
  }
  setLeafDisplayedPath(router.url);

  const emitirPageview = criarEmissorPageviewDaFolha(ref);

  routerEventsSub = router.events
    .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
    .subscribe((event) => {
      setLeafDisplayedPath(event.urlAfterRedirects);
      emitirPageview(event.urlAfterRedirects);
    });
}

/**
 * Liga o emissor de $pageview ao injector da folha.
 *
 * O $pageview do AppComponent não cobre a folha, que bootstrapa o LeafHostComponent; e
 * as navegações internas dela usam pushState, que não chega ao React Router do shell.
 * A subscription de NavigationEnd daqui é, portanto, o único ponto que conhece a rota
 * realmente exibida — o mesmo motivo que a torna a fonte do leaf-displayed-path.
 */
function criarEmissorPageviewDaFolha(ref: NgModuleRef<LeafHostModule>): (url: string) => void {
  const dss = ref.injector.get(DesignSystemSigmaService, null);
  return criarEmissorPageview(ref.injector.get(AbPostHog, null), () =>
    dss?.isDssLayout() ? 'dss' : 'padrao'
  );
}

function resolveLeafRouter(ref: NgModuleRef<LeafHostModule> | null): Router | null {
  return ref?.injector?.get?.(Router, null) ?? null;
}

/** Destrói a folha Angular e limpa overlays/estado de body que vazaram do Angular. */
export function unmountAngularLeaf(): Promise<void> {
  return lifecycle.unmount();
}

async function teardownLeaf(): Promise<void> {
  // Encerra antes do destroy para não processar NavigationEnd durante o teardown,
  // e zera o store para o próximo mount não decidir remount com rota obsoleta.
  routerEventsSub?.unsubscribe();
  routerEventsSub = null;
  resetLeafDisplayedPath();

  // As referências são zeradas ANTES de destruir, porque `moduleRef` é a guarda que
  // diz ao bootstrap se já há algo montado. Um `destroy()` que lance deixaria a
  // guarda apontando para um módulo morto, e o mount seguinte retornaria em silêncio
  // sem criar o <app-leaf-host> — tela vazia sem erro visível. Zeradas antes, a falha
  // do destroy vira um rebootstrap na próxima montagem.
  const module = moduleRef;
  const platform = platformRef;
  moduleRef = null;
  platformRef = null;

  // Encerra o Router pela própria API antes de destruir o módulo.
  //
  // Uma navegação do shell que cruza a fronteira chega à folha como `popstate`, e o
  // Router a agenda num `setTimeout`. Quando o remonte destrói o módulo antes desse
  // timer rodar, a navegação é interrompida pela perda do injector, cai no tratamento
  // de erro do Router e chama `restoreHistory` → `resetUrlToCurrentUrlTree`, que
  // reescreve `window.location` com a rota ANTERIOR da folha. Como `replaceState` não
  // emite evento, a URL volta atrás em silêncio e a navegação que motivou o remonte se
  // perde — o usuário precisa repetir a ação.
  //
  // `dispose` fecha o pipeline de navegação e cancela a inscrição em `popstate`, então
  // a folha moribunda para de agendar e de disputar o hash, sem passar pelo caminho de
  // erro.
  try {
    resolveLeafRouter(module)?.dispose();
    module?.destroy();
  } finally {
    platform?.destroy();
    document.querySelectorAll('app-leaf-host').forEach((el) => el.remove());

    // Limpeza de overlays/toastr/modais anexados ao <body> que vazaram do
    // destroy(). Roda após o destroy: componentes bem comportados fecham seus
    // próprios overlays no ngOnDestroy; isto é a rede de segurança para o resto.
    cleanupAngularOverlays();
    resetBodyLayout();
  }
}
